/**
 * Created by rhinov15 on 29/07/2016.
 */

import { ActionState, ActionStateEnum, EditModeEnum, IdleCameraState, IdleConstruction, IdleDecoration } from "..";
import { EntityManager } from "../../plan/EntityManager";
import { PlanManager } from "../../../../cocosPlan";
import { ArrangementGroup, ArrangementObject, Commands, Entity, Events, eventsManager, math, roomManager, SavaneConstants, SceneConstants, TechnicalElement } from "@rhinov/savane-js";

declare var cc: any;


export class EditEntitiesState extends ActionState {

    // Store state name from global enum
    state: number = ActionStateEnum.EditEntities;
    // Store the entity list we are moving
    entityList: Entity[];
    // Used to close the potential tulip opened previously
    processed = false;
    editMode: number | undefined;
    baseVectorNormalize: math.vec3 | null = null;
    baseAngle: number | null = null;
    rotationCenter = math.vec2.create();

     // Last cursor position during the process to detect change
    _lastCursorPosition: math.vec3 | null = null;
    // Is a dragging started or not
    _dragging: boolean = false;

    ctrlPressed: boolean = false;
    altPressed: boolean = false;

    previousState: number = PlanManager.getInstance().getAction();
    // Store cocosplan clicked coordinate at start
    _clickOffset: math.vec2 = math.vec3.create();

    constructor(entityList: Entity[], position: math.vec3, rotateMode?: number) {
        super();
        this.entityList = entityList;

        // Select all entities but without tulip opened
        PlanManager.getInstance().updateItemsSelected(this.entityList, false, false);

        // Parse all moving entities and start a temprary state for each of them
        for (var i = 0; i < entityList.length; i++) {
            this.entityList[i].startTemporary();
        }

        this.editMode = rotateMode;
       
        for (var i = 0; i < this.entityList.length; ++i) {
            var entity = this.entityList[i];
            this.rotationCenter[0] += entity.position[0];
            this.rotationCenter[1] += entity.position[1];
        }
        this.rotationCenter[0] /= this.entityList.length;
        this.rotationCenter[1] /= this.entityList.length;

        
        math.vec3.set(this._clickOffset, position[0], position[1], 0);
    }

    // Return action name from global enum
    getAction(): number {
        return this.state;
    }

    getEntityType(): number | null  {
        return this.entityList[0].entityType;
    }

    // Key pressed management
    keyPressed(keyCode: number, ctrl?: boolean, alt?: boolean): ActionState | null {
        // Escape key is equivalent to cancelling the operation
        if (keyCode == cc.KEY.escape) {
            return this.mouseClickCancelled(math.vec3.create());
        }
        if (keyCode === cc.KEY.ctrl || ctrl) {
            this.ctrlPressed = true;
        }
        if (keyCode === cc.KEY.alt || alt) {
            this.altPressed = true;
        }

        return null;
    }

    // Key unpressed management
    keyUnPressed(keyCode: number, ctrl?: boolean, maj?: boolean): ActionState | null {
        if (keyCode === cc.KEY.e || keyCode === cc.KEY.i) {
            if (this._dragging) {
                var validEntities = [];
                for (var i = 0; i < this.entityList.length; ++i) {
                    var entity = this.entityList[i];
                    if (entity.isValid(PlanManager.getInstance().world.currentScene!.currentFloor!)) {
                        validEntities.push(entity);
                    }
                    else {
                        entity.endTemporary();
                    }
                }
                if (validEntities.length > 0) {
                    PlanManager.getInstance().executeCommand(new Commands.EditEntitiesCommand(validEntities));
                }
            }
            return this.createIdleState();
        }
        if (keyCode == cc.KEY.ctrl) {
            this.ctrlPressed = false;
        }
        if (keyCode == cc.KEY.alt) {
            this.altPressed = false;
        }
        return null;
    }

    // Mouse click down here, there is nothing to do, the mouse is already down while entering the state
    mouseClickDown(cursorPosition: math.vec3, doubleClick: boolean, hoverEntities: Entity[], mouseButton: number): ActionState | null {
        if (mouseButton === 2) {
            return PlanManager.getInstance().mouseClickCancelled();
        }
        
        return null;
    }

    // Mouse click up management
    mouseClickUp(cursorPosition: math.vec3, elapsedTime: number, hoverEntities: Entity[], mouseButton: number): ActionState | null {
        // Only if the entities were moved (i.e. dragged)
        if (this._dragging) {
            // Let's check if all entities are still valid (not above a wall for instance)
            var allValid = true;

            // Parse them all
            for (var i = 0; i < this.entityList.length; i++) {
                // If it is still temporary, request a validation computation
                if (this.entityList[i].temporary !== null) {
                    this.entityList[i].temporary!.recomputeValidity = true;
                }
                // Check if not valid
                if (!this.entityList[i].isValid(PlanManager.getInstance().world.currentScene!.currentFloor!)) {
                    // Not all valid and quit loop
                    allValid = false;
                    break;
                }
            }

            // If all entities are valid
            if (allValid) {
                // Move all entities thanks to a command (so the move can be undone)
                PlanManager.getInstance().executeCommand(new Commands.EditEntitiesCommand(this.entityList));
            }
            else {
                // Otherwise, replace all entities at their original position thanks to the endTemporary (temporary will be dropped without copying it back)
                for (var i = 0; i < this.entityList.length; i++) {
                    this.entityList[i].endTemporary();
                }
            }
        }

        // Select all moved items at the end of the move
        PlanManager.getInstance().updateItemsSelected(this.entityList, true, mouseButton === 2);

        // Return the correct state to go back to
        return this.createIdleState();
    }

    // Mouse movement management
    moveMouseCursor(cursorPosition: math.vec3, hoverEntities?: Entity[], planpos?: math.vec3): ActionState | null {
        super.moveMouseCursor(cursorPosition);
        // Detect movement and if so, the objects are dragged
        if ((this._lastCursorPosition !== null) && (math.vec3.distance(this._lastCursorPosition!, cursorPosition) > SavaneConstants.PositionTolerance)) {
            this._dragging = true;
        }

        // Close tulip on first move
        if (!this.processed) {
            this.processed = true;
            eventsManager.instance.dispatch(Events.HIDE_TULIP);
        }

        // Parse all edited entities
        for (var i = 0; i < this.entityList.length; i++) {
            var entity: ArrangementObject | TechnicalElement = this.entityList[i] as ArrangementObject | TechnicalElement;
            // Redraw the entity
            var node = EntityManager.getInstance().getNode(entity);
            if (!node) {
                continue;
            }
            node.needRedraw = true;

            switch (this.editMode) {
                case EditModeEnum.rotate:
                    var angle = entity.angle;
                    var currentRotationPoint = math.vec2.fromValues(cursorPosition[0], cursorPosition[1]);
                    var currentVector = math.vec2.fromValues(currentRotationPoint[0] - this.rotationCenter[0], currentRotationPoint[1] - this.rotationCenter[1]);
                    var currentVectorNormalize = math.vec2.create();
                    math.vec2.normalize(currentVectorNormalize, currentVector);
                    if (this.baseVectorNormalize === null) {
                        var cos = Math.cos(Math.PI * 2 - angle);
                        var sin = Math.sin(Math.PI * 2 - angle);
                        this.baseVectorNormalize = math.vec2.create();
                        math.vec2.set(this.baseVectorNormalize, currentVectorNormalize[0] * cos - currentVectorNormalize[1] * sin, currentVectorNormalize[0] * sin + currentVectorNormalize[1] * cos);
                    }
                    //Calculate cosinus of angle using dot product
                    var dotProduct = math.vec2.dot(this.baseVectorNormalize!, currentVectorNormalize);
                    //Calculate angle
                    angle = Math.acos(dotProduct);
                    if (isNaN(angle)) {
                        angle = 0;
                    }
                    //Calculate sign of angle
                    var directionSign = this.baseVectorNormalize![0] * currentVectorNormalize[1] - this.baseVectorNormalize![1] * currentVectorNormalize[0];
                    if (directionSign < 0)
                        angle = -angle;

                    //Apply rotation
                    if (this.ctrlPressed && this.altPressed) {
                        //clamp angle to PI/2
                        entity.angle = Math.round(angle / (Math.PI / 2)) * (Math.PI / 2);
                    } else if (this.ctrlPressed) {
                        //clamp angle to PI/2
                        entity.angle = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
                    } else {
                        entity.angle = angle;
                    }
                    break;
                case EditModeEnum.inclinaison:
                    if (!this._lastCursorPosition) {
                        break;
                    }
                    var cp = math.vec2.fromValues(cursorPosition[0], cursorPosition[1]);
                    var oldRealHeight = entity.realHeight;

                    var factor = 10;
                    if (PlanManager.getInstance().magnetRotation) {
                        factor = 80;
                    }

                    if (!(entity as any).$$dy) {
                        (entity as any).$$dy = 0;
                    }

                    (entity as any).$$dy += (cp[1] - this._lastCursorPosition[1]) / factor;
                    var value = entity.inclinaison + (entity as any).$$dy;

                    if (PlanManager.getInstance().magnetRotation) {
                        value = Math.round(value / 5) * 5;
                    }

                    if (Math.round(entity.inclinaison) !== value) {
                        (entity as any).$$dy = 0;
                        entity.inclinaison = value;
                        var newRealHeight = entity.realHeight;
                        var floorHeight = entity.floorHeight;
                        var itemHeight = entity.height;

                        var oldFloorHeight = floorHeight - ((oldRealHeight / 2) - (itemHeight / 2))
                        var newFloorHeight = oldFloorHeight + ((newRealHeight / 2) - (itemHeight / 2));
                        entity.floorHeight = newFloorHeight;
                    }
                    break;
                default:
                    // Compute new entity position thanks to original position (_transform.globaPosition) and delta with saved mouse plan offset (during class constructor)
                    var computePos = math.vec3.create();
                    var savedPosition = (entity as any)._transform.globalPosition;
                    math.vec3.set(computePos, savedPosition[0] + (cursorPosition[0] - this._clickOffset[0]), savedPosition[1] + (cursorPosition[1] - this._clickOffset[1]), savedPosition[2]);
                    entity.position = computePos;
                    break;
            }

            // If entity has a temporary transform, check its validity
            if (entity.temporary !== null) {
                entity.temporary.recomputeValidity = true;
            }
            // And mark the node with its validity
            node.setValidity(entity.isValid(PlanManager.getInstance().world.currentScene!.currentFloor!));

            // Check entity type to adjust floor height if necessary (only for arrangements)
            switch (entity.entityType) {
                case SceneConstants.EntityType.ArrangementObject:
                case SceneConstants.EntityType.ArrangementGroup:
                    (entity as ArrangementObject | ArrangementGroup).updateFloorHeightFromAnchor(roomManager.getRoomAtPosition(entity.position, PlanManager.getInstance().world.currentScene!.currentFloor!)!, PlanManager.getInstance().world.currentScene!.currentFloor!);
                    break;

                case SceneConstants.EntityType.Staircase:
                case SceneConstants.EntityType.TechnicalElement:
                    break;
            }
            // Update the node
            node.update(undefined);

            // Inform WebGL to update this object as it was moved
            eventsManager.instance.dispatch(Events.COMMAND_EXECUTED, {
                name: Commands.CommandEnum.EditEntityCommand,
                undo: false,
                datas: {
                    id: entity.id,
                    type: entity.entityType,
                    select: true
                }
            });
        }

        this._lastCursorPosition = math.vec3.clone(cursorPosition);
        return null;
    }

    // Mouse right button management, cancl the operation in progress
    mouseClickCancelled(cursorPosition: math.vec3 | null): ActionState | null {
        // Create the next idleState to go to (construction or decoration)
        var idleState = this.createIdleState();

        // Parse all moving entities
        for (var i = 0; i < this.entityList.length; i++) {
            var entity = this.entityList[i];
            // Mark all nodes as not hovered
            var node = EntityManager.getInstance().getNode(entity);
            if (node !== null) {
                node._isHovered = false;
            }

            // Inform WebGL to update this object as it was moved
            eventsManager.instance.dispatch(Events.COMMAND_EXECUTED, {
                name: Commands.CommandEnum.EditEntityCommand,
                undo: false,
                datas: {
                    id: entity.id,
                    type: entity.entityType,
                    select: true
                }
            });

            // Put the entity back to its original position (i.e. end its temprary without saving it)
            entity.endTemporary();
        }

        eventsManager.instance.dispatch(Events.PLAN_STATE_CHANGED, idleState.getAction());

        return idleState;
    }

    // Return if this state is a decoration state
    isDecorationState(): boolean {
        // Return yes only if editing cameras, lights and arrangements
        switch (this.entityList[0].entityType) {
            case SceneConstants.EntityType.UserPicture:
            case SceneConstants.EntityType.RenderCamera:
            case SceneConstants.EntityType.ArrangementObject:
            case SceneConstants.EntityType.ArrangementGroup:
                return (true);
                break;
        }
        return (false);
    }

    // End of this state, replace all entities at their original locations by ending their temporary
    dispose() {
        for (var i = 0; i < this.entityList.length; i++) {
            var entity = this.entityList[i];
            entity.endTemporary();
            delete (entity as any).$$dy;
        }
    }

    // Create the next idle state thanks to the current edited object type.
    createIdleState(): ActionState {
        var state: ActionState | null = null;

        switch (this.entityList[0].entityType) {
            // For cameras, lights and arrangement, go back to idle decoration
            case SceneConstants.EntityType.RenderCamera:
            case SceneConstants.EntityType.ArrangementObject:
            case SceneConstants.EntityType.ArrangementGroup:
                state = new IdleDecoration();
                break;

            // For staircase and technical elements go back to idle construction
            case SceneConstants.EntityType.Staircase:
            case SceneConstants.EntityType.TechnicalElement:
            case SceneConstants.EntityType.Comment:
                state = new IdleConstruction();
                break;

            case SceneConstants.EntityType.GeometryPrimitive:
            case SceneConstants.EntityType.GeometryGroup:
                if (this.previousState === ActionStateEnum.IdleConstruction) {
                    state = new IdleConstruction();
                }
                else {
                    state = new IdleDecoration();
                }
                break;

            // For user pictures, go back to idle camera
            case SceneConstants.EntityType.UserPicture:
                state = new IdleCameraState();
                break;
        }
        return state!;
    }
}